import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { nanoid } from "nanoid";
import { useState, type FC } from "react";
import { Dialog } from "react-aria-components";
import { useNavigate } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";
import { useCreateTrainingProjectMutation } from "../../../api/entities/training.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { useNavigateKeepCopilotChat } from "../../researchAgent/researchAgentPageContext.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import { buildTrainingProposalEditLocation } from "../projectSettings/training/trainingEditorUrlState.js";

export interface CreateTrainingModalProps {
  /**
   * The `create_training` proposal the training comes from. Its name and
   * playbook pre-fill the form, and the created project takes the id the
   * proposal reserved, so the builder can replay the proposal's operations.
   */
  proposal?: {
    id: CopilotProposalId;
    name: string;
    playbookId: PlaybookId;
  };
}

/**
 * Render this inside a react-aria `<DialogTrigger>` alongside the trigger
 * button — the trigger owns the open state. The form lives in
 * {@link CreateTrainingModalContent}, which the modal mounts only while open,
 * so it starts from the proposal (or empty) on every open.
 */
export const CreateTrainingModal: FC<CreateTrainingModalProps> = (props) => (
  <CenteredModal isDismissable>
    <Dialog className="px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
      {({ close }) => (
        <CreateTrainingModalContent proposal={props.proposal} onClose={close} />
      )}
    </Dialog>
  </CenteredModal>
);

interface CreateTrainingModalContentProps extends CreateTrainingModalProps {
  onClose: () => void;
}

const CreateTrainingModalContent: FC<CreateTrainingModalContentProps> = (
  props,
) => {
  const { onClose, proposal } = props;
  const teammate = useAppSelector(selectTeammate);
  if (!teammate) {
    throw new Error("Teammate not found");
  }

  const [createTraining, { isLoading: isCreatingTraining }] =
    useCreateTrainingProjectMutation();

  const { data: playbooks, isLoading: isLoadingPlaybooks } =
    useAllMinimalPlaybooksQuery({
      shouldExcludeTeammateAgentPlaybooks: true,
    });

  const [name, setName] = useState(proposal?.name ?? "");
  const [playbookId, setPlaybookId] = useState<PlaybookId | null>(
    proposal?.playbookId ?? null,
  );

  const navigate = useNavigate();
  const navigateKeepCopilotChat = useNavigateKeepCopilotChat();
  const dispatch = useAppDispatch();

  const playbookOptions: SimpleSelectOption<PlaybookId>[] = (
    playbooks ?? []
  ).map((playbook) => ({
    value: playbook.id,
    label: playbook.name,
  }));

  const handleSubmit = async () => {
    if (!name.trim() || !playbookId) {
      showNotification(dispatch, {
        id: `validation-error-${Date.now()}`,
        primaryMessage: "Please provide a name and select a playbook.",
        type: NotificationType.FAILURE,
      });
      return;
    }

    try {
      const short_id = nanoid();
      const projectData = await createTraining({
        name: name.trim(),
        short_id,
        owner_teammate_ids: [teammate.id],
        playbook_id: playbookId,
        ...(proposal ? { proposal_id: proposal.id } : {}),
      }).unwrap();

      showNotification(dispatch, {
        id: `training-created-${new Date().getTime().toString()}`,
        primaryMessage: `Training successfully created`,
        type: NotificationType.SUCCESS,
      });

      onClose();
      // A training created from a suggestion keeps the chat that suggested it.
      if (proposal) {
        navigateKeepCopilotChat(
          buildTrainingProposalEditLocation({
            projectId: projectData.data.project.id,
            proposalId: proposal.id,
          }),
        );
      } else {
        navigate(
          buildProjectUrlPath(
            projectData.data.project.id,
            ProjectDetailsTabPath.ProjectConfiguration,
          ),
        );
      }
    } catch (err: any) {
      console.error("Error creating training:", err);
      showNotification(dispatch, {
        id: `training-error-${new Date().getTime().toString()}`,
        primaryMessage: `Failed to create training: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <div className="space-y-6">
      <ModalHeader onClose={onClose}>Create training</ModalHeader>

      <div className="space-y-4">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sales team initial training"
          label="Name"
          autoFocus
          maxLength={100}
        />
        <div>
          <SimpleSelect
            options={playbookOptions}
            value={playbookId}
            onChange={setPlaybookId}
            isDisabled={isLoadingPlaybooks}
            placeholder="Select a playbook"
            label="Playbook"
          />
          <div className="mt-2 text-sm text-muted-foreground">
            Assess participants for their ability to succeed in executing
            relevant parts of this playbook.
          </div>
        </div>
        <div className="flex justify-end space-x-3 pt-2">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onClose}
            isDisabled={isCreatingTraining}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmit}
            isDisabled={isCreatingTraining || !name.trim() || !playbookId}
            isLoading={isCreatingTraining}
          >
            Create training
          </Button>
        </div>
      </div>
    </div>
  );
};
